"use client";

import { useEffect, useState } from "react";
import Masonry from "react-masonry-css";

import CustomImage from "@/components/shared/CustomImage";
import { useTranslation } from "@/hooks/useTranslation";
import type { Media } from "@/payload-types";
import getImageUrl from "@/utils/getImageUrl";

interface GalleryItem {
  id: number;
  src: string;
  alt: string;
  media: Media;
}

export default function MasonryGallery({
  masonryGallery,
}: {
  masonryGallery: (number | Media)[] | null | undefined;
}) {
  const { t } = useTranslation();
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [allImages, setAllImages] = useState<GalleryItem[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Process backend data
  useEffect(() => {
    const processImages = () => {
      setIsInitialLoading(true);

      if (!masonryGallery || masonryGallery.length === 0) {
        setAllImages([]);
        setItems([]);
        setHasMore(false);
        setIsInitialLoading(false);
        return;
      }

      // Convert backend data to GalleryItem format
      const processedImages: GalleryItem[] = masonryGallery
        .map((item, index) => {
          // Handle both number and Media types
          if (typeof item === "number") {
            return null; // Skip number items or handle them differently if needed
          }

          const media = item as Media;
          return {
            id: index,
            src: getImageUrl(media),
            alt: media.alt || `Gallery image ${index + 1}`,
            media,
          };
        })
        .filter((item): item is GalleryItem => item !== null);

      setAllImages(processedImages);

      // Set initial items (first 6 images)
      const initialItems = processedImages.slice(0, 6);
      setItems(initialItems);
      setHasMore(processedImages.length > 6);
      setIsInitialLoading(false);
    };

    processImages();
  }, [masonryGallery]);

  const loadMore = () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    setTimeout(() => {
      if (items.length >= allImages.length) {
        setHasMore(false);
        setIsLoading(false);
        return;
      }

      const nextBatch = allImages.slice(items.length, items.length + 6);
      setItems((prev) => [...prev, ...nextBatch]);

      // Check if we've loaded all images
      if (items.length + nextBatch.length >= allImages.length) {
        setHasMore(false);
      }

      setIsLoading(false);
    }, 500);
  };

  const breakpointColumnsObj = {
    default: 3,
    1024: 2,
    640: 1,
  };

  if (isInitialLoading) {
    return (
      <div className="w-full py-8 text-center">
        <p className="text-gray-500">
          {t({
            de: "Bilder werden geladen...",
            en: "Loading images...",
          })}
        </p>
      </div>
    );
  }

  if (!allImages || allImages.length === 0) {
    return (
      <div className="w-full py-8 text-center">
        <p className="text-gray-500">
          {t({
            de: "Keine Bilder verfügbar",
            en: "No images available",
          })}
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Masonry
        breakpointCols={breakpointColumnsObj}
        className="my-masonry-grid"
        columnClassName="my-masonry-grid_column"
      >
        {items.map((item) => (
          <div
            key={item.id}
            className="group relative mb-4 overflow-hidden rounded shadow-lg transition-transform duration-500"
          >
            <CustomImage
              src={item.src}
              alt={item.alt}
              width={400}
              height={300}
              className="h-auto w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
        ))}
      </Masonry>

      {hasMore && (
        <div className="py-8 text-center">
          <button
            onClick={loadMore}
            disabled={isLoading}
            className="mx-auto flex w-fit items-center rounded-full bg-primary-600 px-8 py-4 text-lg font-bold text-white shadow-lg transition-colors duration-500 hover:bg-primary-500"
          >
            {isLoading
              ? t({
                  de: "Lädt...",
                  en: "Loading...",
                })
              : t({
                  de: "Mehr anzeigen",
                  en: "Show More",
                })}
          </button>
        </div>
      )}

      {!hasMore && items.length > 0 && (
        <div className="py-8 text-center">
          <p className="text-gray-500">
            {t({
              de: "Keine weiteren Elemente anzuzeigen",
              en: "No more items to show",
            })}
          </p>
        </div>
      )}
    </div>
  );
}
